// src/modules/ida/hooks/useSendMessageWithFiles.ts
//
// Custom hook for sending chat messages with file attachments.
// Uses multipart/form-data instead of JSON, with SSE streaming response.

import { useCallback, useEffect, useState, useRef } from "react";
import { useChatStore, useJobStream } from "@metalmind-ab/mmagent-chat-ui";
import type {
  ImageReference,
  ChatMessageType,
  ToolExecution,
  Citation,
  BackgroundJob,
} from "@metalmind-ab/mmagent-chat-ui";
import { getToolDisplayName } from "../utils/toolDisplayName";
import { settleCancelledToolExecutions } from "../utils/cancelledToolExecutions";
import { createAssistantTurnRouter } from "../utils/assistantTurnRouting";
import {
  OUTPUT_TRUNCATED_ERROR_CODE,
  OUTPUT_TRUNCATED_NOTICE_KEY,
} from "../utils/outputTruncation";
import { isStreamableJob } from "./useBackgroundJobProgressResilience";
import { appendStreamInterruptedMarkerOnce } from "../../../utils/streamInterruptedMarker";
import {
  hasAcceptedChatTerminal,
  rememberAcceptedChatTerminal,
} from "../../../utils/acceptedChatTerminals";
import type {
  HeartbeatTurnMetadata,
  IDAChatMessage,
} from "../utils/chatMessageMapper";
import {
  nonEmptyDocumentString,
  readDocumentIdentity,
} from "../utils/documentIdentity";
import { shouldIgnoreBackgroundJobTerminalUpdate } from "../utils/backgroundJobTerminal";

export { STREAM_INTERRUPTED_MARKER } from "../../../utils/streamInterruptedMarker";

/** File attachment metadata stored with user messages */
export interface FileAttachment {
  filename: string;
  fileSize: number;
  mimeType: string;
  docId?: string; // Set after file_uploaded event
}

/** Extended ChatMessage with file attachments */
export interface ChatMessageWithFiles extends ChatMessageType {
  fileAttachments?: FileAttachment[];
}

interface UseSendMessageWithFilesOptions {
  apiBaseUrl: string;
  chatSendApiBaseUrl?: string;
  authHeaders: Record<string, string>;
  /** The assistant messages currently shown in the chat. */
  visibleMessages?: IDAChatMessage[];
  /** Conversation that owns visibleMessages; prevents restoring stale history. */
  visibleMessagesConversationId?: string | number | null;
  getAuthHeaders?: () => Promise<Record<string, string>>;
  currentUserId?: string | null;
  onSubmissionId?: (submissionId: string, conversationId: string) => void;
  onTerminalAccepted?: (terminal: {
    submissionId: string;
    kind: "done" | "error";
  }) => void;
  onReplayUnavailable?: (submissionId?: string) => void;
}

interface UseSendMessageWithFilesResult {
  sendWithFiles: (
    content: string,
    files: File[],
    images?: ImageReference[],
  ) => Promise<void>;
  isUploading: boolean;
  cancelLocalStream: () => void;
}

/** SSE event from the backend */
interface SSEEvent {
  type: string;
  content?: string;
  conversation_id?: number;
  user_message_id?: number;
  assistant_message_id?: number;
  assistant_turn_id?: string;
  job_id?: string;
  submission_id?: string;
  doc_id?: string;
  document_id?: string;
  document_title?: string;
  filename?: string;
  file_size?: number;
  mime_type?: string;
  tool_name?: string;
  tool_args?: Record<string, unknown>;
  tool_call_id?: string;
  replaces_tool_call_id?: string;
  status?: "exited" | "completed" | "error";
  result?: Record<string, unknown>;
  citations?: Citation[];
  error?: string;
  error_code?: string;
  message?: string; // For warning events
  skill_name?: string;
  instance_id?: string;
  progress?: number;
  progress_message?: string;
  user_display?: {
    title: string;
    summary: string;
    status: "completed" | "failed";
    details?: Record<string, unknown>;
  };
  llm_context?: string;
  // Whole-surface enrichment for the just-completed assistant turn, carried on
  // the terminal `done` frame so the live-streamed bubble is enriched AT SOURCE
  // (identical to the polled/loaded row from mapStandaloneMessage). Without
  // this, the streamed bubble has no provenance/usage and the per-message
  // usage/provenance chip renders blank until a reload; a poll-side collapse
  // cannot durably fix it because the chat library re-materializes the blank
  // streamed bubble each render. The field mirrors the enrichment subset of the
  // backend's persisted assistant row (snake_case, same serializer as
  // GET /conversations/{id}/messages). Optional so an older backend that does
  // not yet emit it does not crash the handler during the deploy window.
  assistant_message?: {
    metadata?: HeartbeatTurnMetadata;
    model?: string | null;
    provider?: string | null;
    prompt_tokens?: number | null;
    completion_tokens?: number | null;
    total_tokens?: number | null;
  };
}

function buildDocumentJobResult(
  existingResult: Record<string, unknown> | undefined,
  event: SSEEvent,
): Record<string, unknown> {
  const result = { ...existingResult, ...event.result };
  const documentId =
    nonEmptyDocumentString(event.document_id) ??
    nonEmptyDocumentString(event.doc_id) ??
    nonEmptyDocumentString(event.result?.document_id) ??
    nonEmptyDocumentString(existingResult?.document_id);
  const documentTitle =
    nonEmptyDocumentString(event.document_title) ??
    nonEmptyDocumentString(event.filename) ??
    nonEmptyDocumentString(event.result?.document_title) ??
    nonEmptyDocumentString(existingResult?.document_title);

  if (documentId) result.document_id = documentId;
  if (documentTitle) result.document_title = documentTitle;
  return result;
}

// Streaming content buffer flush interval in ms
// Higher values reduce UI freezing but make streaming appear choppier
const STREAM_FLUSH_INTERVAL = 150;

// Task #580 Phase 3: bounded reconnect budget against the BE Last-Event-ID
// resume primitive (PR #2513). Three attempts × backoff matches the QC
// stream resilience pattern in useQualityCheck.ts and the auth-wrapper
// retry budget elsewhere in the FE. Backoff is short-circuited under
// NODE_ENV=test so jest doesn't have to wait real wall-clock seconds.
const MAX_RESUME_ATTEMPTS = 3;
const RESUME_BACKOFF_MS = process.env.NODE_ENV === "test" ? 0 : 2000;

const DOCUMENT_JOB_STORAGE_PREFIX = "ida-document-job-ids:";
const DOCUMENT_JOB_REPLAY_TIMEOUT_MS = 8000;

const documentJobStorageKey = (conversationId: string | number) =>
  `${DOCUMENT_JOB_STORAGE_PREFIX}${conversationId}`;

const readStoredDocumentJobIds = (
  conversationId: string | number,
): string[] => {
  try {
    const stored = window.sessionStorage.getItem(
      documentJobStorageKey(conversationId),
    );
    const value: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(value)
      ? value.filter((jobId): jobId is string => typeof jobId === "string")
      : [];
  } catch {
    return [];
  }
};

const rememberDocumentJobId = (
  conversationId: string | number | null | undefined,
  jobId: string,
) => {
  if (conversationId == null) return;
  try {
    const ids = new Set(readStoredDocumentJobIds(conversationId));
    ids.add(jobId);
    window.sessionStorage.setItem(
      documentJobStorageKey(conversationId),
      JSON.stringify([...ids]),
    );
  } catch {
    // The current chat session still tracks the active job in memory.
  }
};

const forgetDocumentJobId = (
  conversationId: string | number | null | undefined,
  jobId: string,
) => {
  if (conversationId == null) return;
  try {
    const remaining = readStoredDocumentJobIds(conversationId).filter(
      (storedId) => storedId !== jobId,
    );
    if (remaining.length === 0) {
      window.sessionStorage.removeItem(documentJobStorageKey(conversationId));
    } else {
      window.sessionStorage.setItem(
        documentJobStorageKey(conversationId),
        JSON.stringify(remaining),
      );
    }
  } catch {
    // Storage is best-effort; the chat remains authoritative.
  }
};

/**
 * Hook for sending chat messages with file attachments.
 * Handles multipart/form-data uploads with SSE streaming response.
 */
export function useSendMessageWithFiles(
  options: UseSendMessageWithFilesOptions,
): UseSendMessageWithFilesResult {
  const {
    apiBaseUrl,
    chatSendApiBaseUrl = apiBaseUrl,
    authHeaders,
    getAuthHeaders,
    currentUserId,
    onSubmissionId,
    onTerminalAccepted,
    onReplayUnavailable,
  } = options;
  const [isUploading, setIsUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeAssistantMessageIdRef = useRef<string | null>(null);
  const cancelLocalStream = useCallback(() => {
    abortControllerRef.current?.abort();
    settleCancelledToolExecutions(activeAssistantMessageIdRef.current);
  }, []);

  // Track accumulated content length for inline tool positioning
  const contentLengthRef = useRef(0);

  // Streaming content buffer - batch updates to prevent UI freezing
  const contentBufferRef = useRef<{
    messageId: string;
    content: string;
  } | null>(null);
  const flushTimerRef = useRef<number | null>(null);

  // Get store actions
  const addMessage = useChatStore((state) => state.addMessage);
  const updateMessage = useChatStore((state) => state.updateMessage);
  const appendToMessage = useChatStore((state) => state.appendToMessage);
  const setLoading = useChatStore((state) => state.setLoading);
  const setConversationId = useChatStore((state) => state.setConversationId);
  const conversationId = useChatStore((state) => state.conversationId);
  const addToolExecutionToMessage = useChatStore(
    (state) => state.addToolExecutionToMessage,
  );
  const updateToolExecutionInMessage = useChatStore(
    (state) => state.updateToolExecutionInMessage,
  );
  const setCitationsForMessage = useChatStore(
    (state) => state.setCitationsForMessage,
  );
  const addBackgroundJob = useChatStore((state) => state.addBackgroundJob);
  const updateBackgroundJob = useChatStore(
    (state) => state.updateBackgroundJob,
  );
  const { visibleMessages = [], visibleMessagesConversationId } = options;
  const messageConversationId = visibleMessagesConversationId ?? conversationId;
  const restoredDocumentJobIdsRef = useRef(new Set<string>());
  const terminalDocumentReplayJobIdsRef = useRef(new Set<string>());
  const terminalDocumentReplayTimeoutsRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  const jobToolExecutionsRef = useRef(
    new Map<string, { messageId: string; toolCallId: string }>(),
  );
  const jobConversationIdsRef = useRef(new Map<string, string | number>());

  const isCurrentConversation = useCallback(
    (sourceConversationId?: string | number | null) => {
      if (sourceConversationId == null) return true;
      const selectedConversationId = useChatStore.getState().conversationId as
        string | number | null;
      return (
        selectedConversationId != null &&
        String(sourceConversationId) === String(selectedConversationId)
      );
    },
    [],
  );

  const updateToolCardForJob = useCallback(
    (job: BackgroundJob) => {
      const link = jobToolExecutionsRef.current.get(job.jobId);
      const state = useChatStore.getState();
      const target = link
        ? state.messages.find((message) => message.id === link.messageId)
        : state.messages.find((message) =>
            message.toolExecutions?.some(
              (execution) =>
                String(execution.result?.job_id ?? "") === job.jobId,
            ),
          );
      const execution = target?.toolExecutions?.find(
        (item) =>
          item.id === link?.toolCallId ||
          String(item.result?.job_id ?? "") === job.jobId,
      );
      if (!target || !execution) return;
      updateToolExecutionInMessage(target.id, execution.id, {
        status: job.status === "completed" ? "completed" : "error",
        // The background job store owns per-job results. Document cards read
        // their identity there so file and typed sends share one completion
        // source.
        result: { background: true, job_id: job.jobId },
        error: undefined,
      });
      jobToolExecutionsRef.current.delete(job.jobId);
    },
    [updateToolExecutionInMessage],
  );

  // Subscribe to background jobs that publish to the separate
  // /chat/jobs/{id}/stream endpoint. Their per-job stream remains authoritative
  // for progress and replay; document-job terminals can also arrive on the
  // originating chat stream and are applied there as a fallback.
  //
  // useJobStream is intentionally STATELESS (per the library): it parses the
  // per-job SSE and invokes these callbacks; the callbacks are the ONLY thing
  // that writes per-job stream state into the store. Without callbacks,
  // per-job-only terminals would leave the job running. (The library's prop names are
  // onProgress / onCompleted / onFailed -- there is no "Job"-prefixed variant.)
  //
  // Keep lifecycle state in sync and update the original assistant tool card.
  const {
    subscribe: subscribeToJobStream,
    unsubscribe: unsubscribeFromJobStream,
  } = useJobStream({
    apiBaseUrl,
    headers: authHeaders,
    onProgress: (job) => {
      if (
        !isCurrentConversation(jobConversationIdsRef.current.get(job.jobId))
      ) {
        return;
      }
      updateBackgroundJob(job.jobId, {
        progress: job.progress,
        progressMessage: job.progressMessage,
      });
    },
    onCompleted: (job) => {
      unsubscribeFromJobStream(job.jobId);
      const replayTimeout = terminalDocumentReplayTimeoutsRef.current.get(
        job.jobId,
      );
      if (replayTimeout) clearTimeout(replayTimeout);
      terminalDocumentReplayTimeoutsRef.current.delete(job.jobId);
      terminalDocumentReplayJobIdsRef.current.delete(job.jobId);
      const jobConversationId = jobConversationIdsRef.current.get(job.jobId);
      if (!isCurrentConversation(jobConversationId)) {
        // The stream is terminal and will close. Let the restore effect
        // subscribe again when the user returns to this conversation so the
        // server can replay the terminal event into the active chat.
        restoredDocumentJobIdsRef.current.delete(job.jobId);
        return;
      }
      const currentJob = useChatStore
        .getState()
        .backgroundJobs.find((entry) => entry.jobId === job.jobId);
      if (shouldIgnoreBackgroundJobTerminalUpdate(currentJob, "completed"))
        return;
      forgetDocumentJobId(jobConversationId, job.jobId);
      jobConversationIdsRef.current.delete(job.jobId);
      updateBackgroundJob(job.jobId, {
        status: "completed",
        progress: 1,
        completedAt: job.completedAt,
        result: job.result,
      });
      updateToolCardForJob(job);
    },
    onFailed: (job) => {
      unsubscribeFromJobStream(job.jobId);
      const replayTimeout = terminalDocumentReplayTimeoutsRef.current.get(
        job.jobId,
      );
      if (replayTimeout) clearTimeout(replayTimeout);
      terminalDocumentReplayTimeoutsRef.current.delete(job.jobId);
      terminalDocumentReplayJobIdsRef.current.delete(job.jobId);
      const jobConversationId = jobConversationIdsRef.current.get(job.jobId);
      if (!isCurrentConversation(jobConversationId)) {
        restoredDocumentJobIdsRef.current.delete(job.jobId);
        return;
      }
      const currentJob = useChatStore
        .getState()
        .backgroundJobs.find((entry) => entry.jobId === job.jobId);
      if (shouldIgnoreBackgroundJobTerminalUpdate(currentJob, "failed")) return;
      forgetDocumentJobId(jobConversationId, job.jobId);
      jobConversationIdsRef.current.delete(job.jobId);
      updateBackgroundJob(job.jobId, {
        status: "failed",
        progress: job.progress,
        completedAt: job.completedAt,
      });
      updateToolCardForJob(job);
    },
    onError: (error, jobId) => {
      // A history replay can legitimately be gone or inaccessible after the
      // job record expires. The card already has a terminal plain-Created
      // state, so do not surface that best-effort lookup as a user error.
      if (jobId && terminalDocumentReplayJobIdsRef.current.has(jobId)) {
        const replayTimeout =
          terminalDocumentReplayTimeoutsRef.current.get(jobId);
        if (replayTimeout) clearTimeout(replayTimeout);
        terminalDocumentReplayTimeoutsRef.current.delete(jobId);
        terminalDocumentReplayJobIdsRef.current.delete(jobId);
        return;
      }
      console.warn("[IDA Chat] Background job stream error:", error);
    },
  });

  useEffect(() => {
    if (
      conversationId == null ||
      messageConversationId == null ||
      String(messageConversationId) !== String(conversationId)
    ) {
      return;
    }

    const documentExecutions = visibleMessages.flatMap(
      (message) =>
        message.toolExecutions
          ?.filter(
            (execution) =>
              (execution.name === "generate_document" ||
                execution.name === "generate_presentation") &&
              (execution.result?.background === true ||
                typeof execution.result?.job_id === "string"),
          )
          .map((execution) => ({
            execution,
            jobId:
              typeof execution.result?.job_id === "string"
                ? execution.result.job_id
                : undefined,
          })) ?? [],
    );
    const executionByJobId = new Map(
      documentExecutions
        .filter((item): item is typeof item & { jobId: string } =>
          Boolean(item.jobId),
        )
        .map((item) => [item.jobId, item.execution]),
    );
    const storedJobIds = new Set(readStoredDocumentJobIds(conversationId));
    // Only rendered document executions can trigger a history replay. The
    // session-storage list may include jobs whose cards are no longer in the
    // loaded message window.
    const jobIds = new Set(executionByJobId.keys());

    for (const jobId of jobIds) {
      if (restoredDocumentJobIdsRef.current.has(jobId)) continue;
      restoredDocumentJobIdsRef.current.add(jobId);
      jobConversationIdsRef.current.set(jobId, conversationId);

      const existingJob = useChatStore
        .getState()
        .backgroundJobs.find((job) => job.jobId === jobId);
      const execution = executionByJobId.get(jobId);
      const persistedResult = execution?.result;
      const persistedIdentity = readDocumentIdentity(persistedResult);
      const isActive =
        existingJob?.status === "pending" || existingJob?.status === "running";
      const persistedFailure = execution?.status === "error";

      if (
        existingJob?.status === "failed" ||
        existingJob?.status === "cancelled"
      ) {
        continue;
      }

      if (!existingJob) {
        addBackgroundJob({
          jobId,
          skillName: execution?.name ?? "generate_document",
          status: persistedFailure ? "failed" : "completed",
          progress: persistedFailure ? 0 : 1,
          ...(persistedIdentity ? { result: persistedIdentity } : {}),
          ...(persistedFailure && execution?.error
            ? { error: execution.error }
            : {}),
        });
      } else if (
        persistedIdentity &&
        !readDocumentIdentity(existingJob.result)
      ) {
        updateBackgroundJob(jobId, {
          result: { ...existingJob.result, ...persistedIdentity },
        });
      }

      // A job omitted from the page's active_jobs list is already terminal.
      // Show its plain terminal state immediately while the durable stream
      // replays identity. Active jobs keep their normal in-progress state.
      const needsIdentityReplay =
        !isActive &&
        !persistedFailure &&
        !persistedIdentity &&
        (!nonEmptyDocumentString(existingJob?.result?.document_id) ||
          !nonEmptyDocumentString(existingJob?.result?.document_title));
      // Active jobs without a same-tab subscription are watched by the app's
      // resilience subscriber. Keep the package subscription for same-tab
      // jobs, and use a bounded replay for completed history.
      if (isActive && !storedJobIds.has(jobId)) continue;
      if (
        persistedFailure ||
        persistedIdentity ||
        (!isActive && !needsIdentityReplay)
      ) {
        continue;
      }

      const skillName =
        execution?.name ?? existingJob?.skillName ?? "generate_document";
      if (
        skillName !== "generate_document" &&
        skillName !== "generate_presentation"
      ) {
        continue;
      }
      subscribeToJobStream(jobId, skillName);

      if (needsIdentityReplay) {
        terminalDocumentReplayJobIdsRef.current.add(jobId);
        const timeout = setTimeout(() => {
          unsubscribeFromJobStream(jobId);
          terminalDocumentReplayTimeoutsRef.current.delete(jobId);
          terminalDocumentReplayJobIdsRef.current.delete(jobId);
          jobConversationIdsRef.current.delete(jobId);
        }, DOCUMENT_JOB_REPLAY_TIMEOUT_MS);
        terminalDocumentReplayTimeoutsRef.current.set(jobId, timeout);
      }
    }
  }, [
    addBackgroundJob,
    conversationId,
    subscribeToJobStream,
    unsubscribeFromJobStream,
    updateBackgroundJob,
    visibleMessages,
    messageConversationId,
  ]);

  useEffect(
    () => () => {
      for (const [
        jobId,
        timeout,
      ] of terminalDocumentReplayTimeoutsRef.current) {
        clearTimeout(timeout);
        unsubscribeFromJobStream(jobId);
      }
      terminalDocumentReplayTimeoutsRef.current.clear();
      terminalDocumentReplayJobIdsRef.current.clear();
    },
    [unsubscribeFromJobStream],
  );

  // Flush buffered content to the store - batches multiple SSE chunks into single update
  const flushContentBuffer = useCallback(() => {
    if (contentBufferRef.current && contentBufferRef.current.content) {
      appendToMessage(
        contentBufferRef.current.messageId,
        contentBufferRef.current.content,
      );
      contentBufferRef.current.content = "";
    }
  }, [appendToMessage]);

  const sendWithFiles = useCallback(
    async (content: string, files: File[], images?: ImageReference[]) => {
      if (isUploading) return;

      setIsUploading(true);
      setLoading(true);

      // Reset content length tracking for inline tool positioning
      contentLengthRef.current = 0;
      // Reset content buffer for new streaming session
      contentBufferRef.current = null;
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Generate IDs for messages
      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessageIdRef = { current: assistantMessageId };
      activeAssistantMessageIdRef.current = assistantMessageId;
      const assistantTurnRouter = createAssistantTurnRouter(assistantMessageId);

      // Prepare file attachments metadata (will be updated with docIds after upload)
      const fileAttachments: FileAttachment[] = files.map((file) => ({
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type || "application/octet-stream",
      }));

      // Add user message with file attachments immediately
      const userMessage: ChatMessageWithFiles = {
        id: userMessageId,
        role: "user",
        content,
        createdAt: new Date(),
        fileAttachments,
        ...(images && images.length > 0 ? { images } : {}),
        ...(currentUserId
          ? {
              metadata: {
                sender_user_id: currentUserId,
                message_origin: "user",
              },
            }
          : {}),
      };
      addMessage(userMessage as ChatMessageType);

      // Add placeholder assistant message
      const assistantMessage: ChatMessageType = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        createdAt: new Date(),
        isStreaming: true,
      };
      addMessage(assistantMessage);

      let postRejected = false;
      try {
        // Build FormData
        const formData = new FormData();
        formData.append("content", content);
        files.forEach((file) => {
          formData.append("files", file);
        });
        // Mixed send: image references ride the same multipart request as a
        // JSON string of the ImageReference objects POST /chat/images returned.
        if (images && images.length > 0) {
          formData.append("images", JSON.stringify(images));
        }

        // Determine endpoint based on whether we have a conversation ID
        const endpoint = conversationId
          ? `${chatSendApiBaseUrl}/chat/conversations/${conversationId}/messages/stream/with-files`
          : `${chatSendApiBaseUrl}/chat/messages/stream/with-files`;

        // Get fresh headers to avoid using expired tokens
        const freshHeaders = getAuthHeaders
          ? await getAuthHeaders()
          : authHeaders;

        // Make the request
        const response = await fetch(endpoint, {
          method: "POST",
          headers: freshHeaders, // No Content-Type - browser sets it for FormData
          body: formData,
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          postRejected = true;
          throw new Error(`Upload failed: ${response.status} ${errorText}`);
        }
        const submissionId = response.headers?.get("X-Chat-Submission-ID");
        const responseConversationId = response.headers?.get(
          "X-Chat-Conversation-ID",
        );
        if (submissionId && responseConversationId) {
          onSubmissionId?.(submissionId, responseConversationId);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        // Task #580 Phase 3 stream-consume loop.
        //
        // Refactored into a helper closure so the initial POST stream and
        // any subsequent Last-Event-ID resume streams (GET) share the same
        // event-parsing path. Returns the exit condition so the caller can
        // decide whether to reconnect.
        let uploadedFileIndex = 0;
        let lastFrameId: string | null = null;
        let conversationIdForResume: number | null =
          typeof conversationId === "number" ? conversationId : null;
        let requestConversationIdForEvents: string | number | null =
          conversationId;
        if (responseConversationId) {
          requestConversationIdForEvents = responseConversationId;
        }

        const consumeStream = async (
          body: ReadableStream<Uint8Array>,
        ): Promise<{
          receivedDoneEvent: boolean;
          replayUnavailable: boolean;
          replayUnavailableNeedsMarker: boolean;
        }> => {
          const reader = body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let receivedDoneEvent = false;
          let replayUnavailable = false;
          let replayUnavailableNeedsMarker = false;
          let pendingId: string | null = null;

          while (true) {
            let read: ReadableStreamReadResult<Uint8Array>;
            try {
              read = await reader.read();
            } catch {
              // A worker replacement can terminate an established response
              // body with a read error rather than a clean EOF. Treat it as
              // an interrupted stream so the existing Last-Event-ID resume
              // loop gets the same opportunity to recover.
              break;
            }
            // A frame already buffered by the browser can resolve after Abort.
            // Confirmed Stop has settled this turn's tools; do not revive them.
            if (abortControllerRef.current?.signal.aborted) break;
            const { done, value } = read;
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line in buffer

            let streamDone = false;
            for (const line of lines) {
              // Task #580 Phase 3: capture `id:` line (frame_id from BE
              // PR #2513). Browser auto-reconnect for native EventSource
              // would use this via Last-Event-ID; we use it explicitly on
              // our fetch-reader-based resume.
              if (line.startsWith("id: ")) {
                pendingId = line.slice(4).trim() || null;
                continue;
              }
              if (line === "" || line === "\r") {
                // SSE event terminator — flush pending id to lastFrameId.
                if (pendingId) {
                  lastFrameId = pendingId;
                  pendingId = null;
                }
                continue;
              }
              if (!line.startsWith("data: ")) continue;
              const data = line.slice(6).trim();
              if (!data || data === "[DONE]") continue;

              try {
                const event: SSEEvent = JSON.parse(data);
                // Capture conversation_id wherever it appears on an event
                // so the Last-Event-ID resume endpoint has a target. The
                // existing handleSSEEvent code also calls setConversationId
                // on multiple event types (content, done) — mirror that
                // promiscuity here for the resume cursor.
                if (
                  typeof event.conversation_id === "number" &&
                  conversationIdForResume === null
                ) {
                  conversationIdForResume = event.conversation_id;
                }
                if (
                  event.conversation_id !== undefined &&
                  requestConversationIdForEvents === null
                ) {
                  requestConversationIdForEvents = event.conversation_id;
                }
                // Task #580 Phase 3: BE signals cursor-miss on resume.
                // We've already shown the user partial content; the BE
                // is about to replay the entire buffer, so we drop our
                // local partial and rely on the replay.
                if (event.type === "replay_unavailable") {
                  replayUnavailable = true;
                  replayUnavailableNeedsMarker = !hasAcceptedChatTerminal(
                    event.submission_id,
                  );
                  onReplayUnavailable?.(event.submission_id);
                  continue;
                }
                const route = assistantTurnRouter.route(event);
                if (!route.accepted) continue;
                if (route.started) {
                  const previousMessageId = assistantMessageIdRef.current;
                  assistantMessageIdRef.current = route.messageId;
                  activeAssistantMessageIdRef.current = route.messageId;
                  const state = useChatStore.getState();
                  state.setMessages(
                    state.messages.map((message) =>
                      message.id === previousMessageId
                        ? {
                            ...message,
                            id: route.messageId,
                            metadata: {
                              ...((message as IDAChatMessage).metadata ?? {}),
                              assistant_turn_id: route.messageId,
                            },
                            // This timestamp is the observed turn-start frame,
                            // so concurrently accepted turns remain ordered by
                            // the time their server-owned turns began.
                            createdAt: new Date(),
                          }
                        : message,
                    ),
                  );
                  if (
                    contentBufferRef.current?.messageId === previousMessageId
                  ) {
                    contentBufferRef.current.messageId = route.messageId;
                  }
                  continue;
                }
                // eslint-disable-next-line no-loop-func
                handleSSEEvent(
                  event,
                  userMessageId,
                  assistantMessageIdRef.current,
                  fileAttachments,
                  uploadedFileIndex,
                  // The parser processes events sequentially and updates the
                  // rolling upload index for the next event in this stream.
                  // eslint-disable-next-line no-loop-func
                  (newIndex) => {
                    uploadedFileIndex = newIndex;
                  },
                  requestConversationIdForEvents,
                );
                // Bump lastFrameId for the SSE event we just processed
                // even if no terminator arrived yet -- the BE delivers
                // a frame_id per event by spec (PR #2513).
                if (pendingId) {
                  lastFrameId = pendingId;
                  pendingId = null;
                }
                // `error` is terminal like `done`: the server ends the turn
                // with it (submission_terminal:error) and sends nothing
                // further. Treating it as an unfinished stream made the resume
                // loop below reattach to the conversation and the bubble then
                // got the interruption marker appended to its error text.
                if (event.type === "done" || event.type === "error") {
                  const acceptedSubmissionId =
                    event.submission_id ?? submissionId;
                  if (acceptedSubmissionId) {
                    rememberAcceptedChatTerminal(
                      acceptedSubmissionId,
                      event.type,
                    );
                    onTerminalAccepted?.({
                      submissionId: acceptedSubmissionId,
                      kind: event.type,
                    });
                  }
                  streamDone = true;
                  receivedDoneEvent = true;
                }
              } catch (parseError) {
                console.warn("Failed to parse SSE event:", data, parseError);
              }
            }
            if (streamDone) break;
          }

          return {
            receivedDoneEvent,
            replayUnavailable,
            replayUnavailableNeedsMarker,
          };
        };

        let {
          receivedDoneEvent,
          replayUnavailable,
          replayUnavailableNeedsMarker,
        } = await consumeStream(response.body);

        // Task #580 Phase 3: transparent reconnect on a clean network drop.
        // The Last-Event-ID resume endpoint requires a conversation_id, so
        // skip the resume path when the stream died before the BE assigned
        // one (very early drops). Resume is bounded by MAX_RESUME_ATTEMPTS;
        // a `replay_unavailable` event ends the loop early -- the BE has
        // already drained the buffer to us.
        let resumeAttempts = 0;
        while (
          !receivedDoneEvent &&
          !replayUnavailable &&
          conversationIdForResume !== null &&
          lastFrameId !== null &&
          resumeAttempts < MAX_RESUME_ATTEMPTS
        ) {
          resumeAttempts++;
          const backoffMs = RESUME_BACKOFF_MS * resumeAttempts;
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          if (abortControllerRef.current?.signal.aborted) break;

          const resumeEndpoint = `${apiBaseUrl}/chat/conversations/${conversationIdForResume}/messages/stream`;
          const resumeHeaders = getAuthHeaders
            ? await getAuthHeaders()
            : authHeaders;
          let resumeResponse: Response;
          try {
            resumeResponse = await fetch(resumeEndpoint, {
              method: "GET",
              headers: {
                ...resumeHeaders,
                "Last-Event-ID": lastFrameId,
              },
              signal: abortControllerRef.current?.signal,
            });
          } catch (resumeErr) {
            console.warn(
              `[IDA Chat] Resume attempt ${resumeAttempts} failed to connect:`,
              resumeErr,
            );
            continue;
          }
          if (!resumeResponse.ok || !resumeResponse.body) {
            console.warn(
              `[IDA Chat] Resume attempt ${resumeAttempts} returned ${resumeResponse.status}`,
            );
            continue;
          }
          ({
            receivedDoneEvent,
            replayUnavailable,
            replayUnavailableNeedsMarker,
          } = await consumeStream(resumeResponse.body));
        }

        // Flush any remaining buffered content before marking complete
        if (flushTimerRef.current !== null) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        flushContentBuffer();

        // Task #497 A1 / Task #580 Phase 3: append the interruption marker
        // when the stream ended without a clean BE done event. Transparent
        // resume (done received on a reconnect attempt) leaves the marker
        // off so the user sees the natural completion. `replay_unavailable`
        // is treated as an unrecoverable interruption from the user's POV
        // because the BE replayed the buffer wholesale and we may have lost
        // the cursor's earlier frames -- mark it.
        const shouldMarkInterrupted = replayUnavailable
          ? replayUnavailableNeedsMarker
          : !receivedDoneEvent;
        if (
          shouldMarkInterrupted &&
          !abortControllerRef.current?.signal.aborted
        ) {
          console.warn(
            `[IDA Chat] Stream ended interrupted (done=${receivedDoneEvent}, replayUnavailable=${replayUnavailable}, resumeAttempts=${resumeAttempts}); marking.`,
          );
          appendStreamInterruptedMarkerOnce(assistantMessageIdRef.current);
        }

        // Mark streaming as complete
        updateMessage(assistantMessageIdRef.current, { isStreaming: false });
      } catch (error) {
        if (postRejected) {
          // A failed POST never became a chat turn. Remove only this request's
          // optimistic rows and let the composer restore the draft.
          const state = useChatStore.getState();
          state.setMessages(
            state.messages.filter(
              (message) =>
                message.id !== userMessageId &&
                message.id !== assistantMessageIdRef.current,
            ),
          );
          throw error;
        }
        // Flush buffer on error too to ensure partial content is shown
        if (flushTimerRef.current !== null) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        flushContentBuffer();

        if ((error as Error).name === "AbortError") {
          console.log("Upload aborted");
          // Stop pressed before any frame arrived: nothing else settles the
          // empty bubble, and the "Working..." indicator (#5327) would spin.
          updateMessage(assistantMessageIdRef.current, { isStreaming: false });
        } else {
          console.error("Upload failed:", error);
          updateMessage(assistantMessageIdRef.current, {
            content: `Error: ${(error as Error).message}`,
            isStreaming: false,
          });
        }
      } finally {
        setIsUploading(false);
        setLoading(false);
        abortControllerRef.current = null;
        activeAssistantMessageIdRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      apiBaseUrl,
      chatSendApiBaseUrl,
      authHeaders,
      getAuthHeaders,
      conversationId,
      onSubmissionId,
      isUploading,
      addMessage,
      updateMessage,
      setLoading,
      setConversationId,
      addToolExecutionToMessage,
      updateToolExecutionInMessage,
      currentUserId,
      setCitationsForMessage,
      addBackgroundJob,
      appendToMessage,
      flushContentBuffer,
    ],
  );

  // Handle individual SSE events
  const handleSSEEvent = useCallback(
    (
      event: SSEEvent,
      userMessageId: string,
      assistantMessageId: string,
      fileAttachments: FileAttachment[],
      uploadedFileIndex: number,
      setUploadedFileIndex: (index: number) => void,
      requestConversationId?: string | number | null,
    ) => {
      switch (event.type) {
        case "file_uploaded":
          // Update file attachment with docId
          if (uploadedFileIndex < fileAttachments.length && event.doc_id) {
            fileAttachments[uploadedFileIndex].docId = event.doc_id;
            setUploadedFileIndex(uploadedFileIndex + 1);
            // Update the user message with the new docId
            updateMessage(userMessageId, {
              fileAttachments: [...fileAttachments],
            } as Partial<ChatMessageType>);
          }
          break;

        case "content_delta":
          if (event.content) {
            // Track content length for inline tool positioning
            contentLengthRef.current += event.content.length;

            // Buffer content and schedule flush - prevents UI freezing during fast streaming
            if (
              !contentBufferRef.current ||
              contentBufferRef.current.messageId !== assistantMessageId
            ) {
              contentBufferRef.current = {
                messageId: assistantMessageId,
                content: event.content,
              };
            } else {
              contentBufferRef.current.content += event.content;
            }

            // Schedule flush if not already scheduled
            if (flushTimerRef.current === null) {
              flushTimerRef.current = window.setTimeout(() => {
                flushTimerRef.current = null;
                if (
                  contentBufferRef.current &&
                  contentBufferRef.current.content
                ) {
                  appendToMessage(
                    contentBufferRef.current.messageId,
                    contentBufferRef.current.content,
                  );
                  contentBufferRef.current.content = "";
                }
              }, STREAM_FLUSH_INTERVAL);
            }
          }
          if (event.conversation_id && !conversationId) {
            setConversationId(event.conversation_id);
          }
          break;

        case "content_done":
          // Content streaming finished -- replace with clean content
          // (strips <tool_summary> tags that leaked during streaming)
          if (event.content) {
            // Flush any pending buffer first
            if (flushTimerRef.current !== null) {
              clearTimeout(flushTimerRef.current);
              flushTimerRef.current = null;
            }
            contentBufferRef.current = null;
            updateMessage(assistantMessageId, {
              content: event.content,
            } as Partial<ChatMessageType>);
          }
          break;

        case "tool_calling":
          // LLM is generating tool call arguments -- show early indicator
          if (event.tool_call_id) {
            // Flush any pending content first
            if (flushTimerRef.current !== null) {
              clearTimeout(flushTimerRef.current);
              flushTimerRef.current = null;
            }
            if (contentBufferRef.current && contentBufferRef.current.content) {
              appendToMessage(
                contentBufferRef.current.messageId,
                contentBufferRef.current.content,
              );
              contentBufferRef.current.content = "";
            }

            const pendingExecution: ToolExecution = {
              id: event.tool_call_id,
              name:
                getToolDisplayName(event.tool_name) ||
                event.tool_name ||
                "preparing",
              args: {},
              status: "running",
              contentPosition: contentLengthRef.current,
            };
            addToolExecutionToMessage(assistantMessageId, pendingExecution);
          }
          break;

        case "tool_start":
          if (event.tool_name && event.tool_call_id) {
            // Flush any pending content first to ensure UI is up-to-date before badge appears
            if (flushTimerRef.current !== null) {
              clearTimeout(flushTimerRef.current);
              flushTimerRef.current = null;
            }
            if (contentBufferRef.current && contentBufferRef.current.content) {
              appendToMessage(
                contentBufferRef.current.messageId,
                contentBufferRef.current.content,
              );
              contentBufferRef.current.content = "";
            }

            // Update this call's own execution from tool_calling, or add it
            // when the early event was absent. A replacement ID links the
            // instruction read to this new execution; it must not overwrite
            // the earlier row.
            updateToolExecutionInMessage(
              assistantMessageId,
              event.tool_call_id,
              {
                name: getToolDisplayName(event.tool_name) || event.tool_name,
                args: event.tool_args || {},
                status: "running",
              },
            );

            // Fallback: if tool_calling was never received, add the execution
            const msg = useChatStore
              .getState()
              .messages.find((m) => m.id === assistantMessageId);
            const hasExecution = msg?.toolExecutions?.some(
              (te) => te.id === event.tool_call_id,
            );
            if (!hasExecution) {
              const execution: ToolExecution = {
                id: event.tool_call_id,
                name: getToolDisplayName(event.tool_name) || event.tool_name,
                args: event.tool_args || {},
                status: "running",
                contentPosition: contentLengthRef.current,
              };
              addToolExecutionToMessage(assistantMessageId, execution);
            }
          }
          break;

        case "tool_end":
          if (event.tool_call_id) {
            const isDocumentJobTool =
              event.tool_name === "generate_document" ||
              event.tool_name === "generate_presentation";
            const isFailedTool =
              event.status === "error" || event.status === "exited";
            // A successful tool_end only means the background job was
            // submitted. Keep its card pending until the per-job completion
            // carries the document identity used by the Created state.
            if (isDocumentJobTool && !isFailedTool) break;
            updateToolExecutionInMessage(
              assistantMessageId,
              event.tool_call_id,
              {
                status:
                  event.status === "exited"
                    ? "exited"
                    : event.status === "error"
                      ? "error"
                      : "completed",
                result: event.result,
              },
            );
          }
          break;

        case "citations":
          if (event.citations) {
            setCitationsForMessage(assistantMessageId, event.citations);
          }
          break;

        case "error":
          if (event.error_code === OUTPUT_TRUNCATED_ERROR_CODE) {
            // A length cap ends an otherwise useful answer. Flush any final
            // buffered tokens before appending the backend's safe notice.
            if (flushTimerRef.current !== null) {
              clearTimeout(flushTimerRef.current);
              flushTimerRef.current = null;
            }
            flushContentBuffer();
            const notice =
              event.error?.trim() || "The answer reached its length limit.";
            const current = useChatStore
              .getState()
              .messages.find((message) => message.id === assistantMessageId) as
              IDAChatMessage | undefined;
            appendToMessage(
              assistantMessageId,
              current?.content ? `\n\n${notice}` : notice,
            );
            updateMessage(assistantMessageId, {
              metadata: {
                ...(current?.metadata ?? {}),
                [OUTPUT_TRUNCATED_NOTICE_KEY]: notice,
              },
              isStreaming: false,
              isError: true,
            } as Partial<ChatMessageType>);
            const state = useChatStore.getState();
            state.setMessages([...state.messages]);
            break;
          }
          // Render the BE's server-owned error verbatim (Task #49 typed-error
          // contract: the backend sends the user-facing message; the FE does
          // not re-genericize it).
          updateMessage(assistantMessageId, {
            content: event.error?.trim() || "Unknown error",
            isStreaming: false,
            isError: true,
          });
          // Force re-render: updateMessage mutates in-place without changing
          // the messages array reference, so components using selector
          // `state => state.messages` won't re-render and the generating chip
          // wouldn't visually clear on a terminal error frame (the broker now
          // replays a buffered terminal `error` event — API PR #3208). Mirror
          // the `done` case: create a new array reference to trip the zustand
          // subscription so the chip clears immediately on error.
          {
            const state = useChatStore.getState();
            state.setMessages([...state.messages]);
          }
          break;

        case "warning":
          // Append warning message to the assistant's response
          if (event.message) {
            appendToMessage(assistantMessageId, `\n\n⚠️ ${event.message}`);
          }
          break;

        case "done": {
          if (event.conversation_id) {
            setConversationId(event.conversation_id);
          }
          // Attach the whole-surface enrichment carried on the done frame so the
          // streamed bubble is enriched AT SOURCE — the per-message
          // usage/provenance chip populates immediately and the blank bubble
          // never exists for the chat library to re-materialize each render (a
          // poll-side collapse cannot durably fix that race). Also set
          // hubMessageIds:[id] so the streamed bubble is shape-identical to the
          // polled/loaded row (mapStandaloneMessage): a poll that races in then
          // sees them as equivalent (areMessagesEquivalent) — no duplicate
          // bubble, no React key collision. Enrichment is attached only when
          // present; an older backend that omits `assistant_message` still
          // settles the bubble (isStreaming:false) without crashing during the
          // deploy window. This is deploy-window tolerance only, NOT a
          // permanent blank fallback: post-cutover every live send carries the
          // row, so a missing chip surfaces via the fresh-send re-verify rather
          // than being silently masked.
          const enrichment = event.assistant_message;
          updateMessage(assistantMessageId, {
            ...(event.assistant_message_id
              ? {
                  hubMessageId: event.assistant_message_id,
                  hubMessageIds: [event.assistant_message_id],
                }
              : {}),
            ...(enrichment
              ? {
                  metadata: {
                    ...((
                      useChatStore
                        .getState()
                        .messages.find(
                          (message) => message.id === assistantMessageId,
                        ) as IDAChatMessage | undefined
                    )?.metadata ?? {}),
                    ...(enrichment.metadata ?? {}),
                  },
                  model: enrichment.model ?? undefined,
                  provider: enrichment.provider ?? undefined,
                  prompt_tokens: enrichment.prompt_tokens ?? undefined,
                  completion_tokens: enrichment.completion_tokens ?? undefined,
                  total_tokens: enrichment.total_tokens ?? undefined,
                }
              : {}),
            isStreaming: false,
          } as Partial<IDAChatMessage> as Partial<ChatMessageType>);
          // Force re-render: updateMessage mutates in-place without changing
          // the messages array reference, so components using selector
          // `state => state.messages` won't re-render. Creating a new array
          // reference triggers the zustand subscription.
          {
            const state = useChatStore.getState();
            state.setMessages([...state.messages]);
          }
          break;
        }

        case "job_started":
          if (event.job_id && event.skill_name) {
            const sourceConversationId =
              event.conversation_id ?? requestConversationId ?? conversationId;
            if (!isCurrentConversation(sourceConversationId)) break;
            if (sourceConversationId != null) {
              jobConversationIdsRef.current.set(
                event.job_id,
                sourceConversationId,
              );
            }
            if (event.skill_name === "generate_document") {
              rememberDocumentJobId(sourceConversationId, event.job_id);
            }
            const job: BackgroundJob = {
              jobId: event.job_id,
              skillName: event.skill_name,
              instanceId: event.instance_id,
              status: "running",
              progress: 0,
              progressMessage: event.progress_message,
            };
            addBackgroundJob(job);
            const state = useChatStore.getState();
            // `state.messages` contains the selected conversation. Tool
            // completion may follow tool_end, so match by call id regardless
            // of execution status and across all its assistant messages.
            const matchingMessage = event.tool_call_id
              ? state.messages.find((message) =>
                  message.toolExecutions?.some(
                    (execution) => execution.id === event.tool_call_id,
                  ),
                )
              : undefined;
            const messageId =
              matchingMessage?.id ?? activeAssistantMessageIdRef.current;
            if (messageId) {
              const existingExecution = state.messages
                .find((message) => message.id === messageId)
                ?.toolExecutions?.find(
                  (execution) => execution.id === event.tool_call_id,
                );
              // A mismatched or absent call id gets a distinct synthetic card;
              // it cannot duplicate an execution with another id.
              const toolCallId = existingExecution
                ? existingExecution.id
                : `background-job-${event.job_id}`;
              jobToolExecutionsRef.current.set(event.job_id, {
                messageId,
                toolCallId,
              });
              if (existingExecution) {
                // A tool_calling/tool_start frame may have already created the
                // real execution card. Attach the job id to that same card so
                // the separate job stream can update it on completion.
                updateToolExecutionInMessage(messageId, existingExecution.id, {
                  result: { background: true, job_id: event.job_id },
                });
              } else {
                addToolExecutionToMessage(messageId, {
                  id: toolCallId,
                  name: event.skill_name,
                  args: event.tool_args ?? {},
                  status: "running",
                  result: {
                    success: true,
                    background: true,
                    job_id: event.job_id,
                  },
                  contentPosition: contentLengthRef.current,
                });
              }
            }
            // Subscribe via the library's useJobStream for background jobs
            // that publish to the separate /chat/jobs/{id}/stream endpoint.
            // Terminal results update this assistant message's tool execution.
            // Inline jobs report progress and completion on this chat stream.
            if (
              isStreamableJob(job) &&
              event.skill_name !== "generate_document" &&
              event.skill_name !== "generate_presentation"
            ) {
              restoredDocumentJobIdsRef.current.add(event.job_id);
              subscribeToJobStream(event.job_id, event.skill_name);
            }
          }
          break;

        case "job_progress":
          if (event.job_id) {
            const sourceConversationId =
              event.conversation_id ??
              requestConversationId ??
              jobConversationIdsRef.current.get(event.job_id) ??
              conversationId;
            if (!isCurrentConversation(sourceConversationId)) break;
            updateBackgroundJob(event.job_id, {
              progress: event.progress ?? 0,
              progressMessage: event.progress_message,
            });
          }
          break;

        case "job_completed":
          // Keep lifecycle state current and apply the result to the existing tool card.
          if (event.job_id) {
            const sourceConversationId =
              event.conversation_id ??
              requestConversationId ??
              jobConversationIdsRef.current.get(event.job_id) ??
              conversationId;
            if (!isCurrentConversation(sourceConversationId)) break;
            const currentJob = useChatStore
              .getState()
              .backgroundJobs.find((job) => job.jobId === event.job_id);
            if (
              shouldIgnoreBackgroundJobTerminalUpdate(currentJob, "completed")
            ) {
              forgetDocumentJobId(sourceConversationId, event.job_id);
              jobConversationIdsRef.current.delete(event.job_id);
              break;
            }
            const skillName = event.skill_name ?? currentJob?.skillName;
            const documentJob =
              skillName === "generate_document" ||
              skillName === "generate_presentation";
            forgetDocumentJobId(sourceConversationId, event.job_id);
            jobConversationIdsRef.current.delete(event.job_id);
            const result = documentJob
              ? buildDocumentJobResult(currentJob?.result, event)
              : undefined;
            updateBackgroundJob(event.job_id, {
              status: "completed",
              progress: 1,
              completedAt: new Date(),
              ...(result ? { result } : {}),
            });
            updateToolCardForJob({
              jobId: event.job_id,
              skillName: skillName ?? "background_skill",
              status: "completed",
              progress: 1,
              ...(result ? { result } : {}),
            });
          }
          break;

        case "job_failed":
          // Keep failed-job lifecycle state in sync with the per-job stream.
          if (event.job_id) {
            const sourceConversationId =
              event.conversation_id ??
              requestConversationId ??
              jobConversationIdsRef.current.get(event.job_id) ??
              conversationId;
            if (!isCurrentConversation(sourceConversationId)) break;
            forgetDocumentJobId(sourceConversationId, event.job_id);
            jobConversationIdsRef.current.delete(event.job_id);
            const currentJob = useChatStore
              .getState()
              .backgroundJobs.find((job) => job.jobId === event.job_id);
            if (shouldIgnoreBackgroundJobTerminalUpdate(currentJob, "failed"))
              break;
            updateBackgroundJob(event.job_id, {
              status: "failed",
              progress: event.progress ?? 0,
              completedAt: new Date(),
            });
            updateToolCardForJob({
              jobId: event.job_id,
              skillName: event.skill_name ?? "background_skill",
              status: "failed",
              progress: event.progress ?? 0,
            });
          }
          break;
      }
    },
    [
      conversationId,
      appendToMessage,
      updateMessage,
      setConversationId,
      addToolExecutionToMessage,
      updateToolExecutionInMessage,
      setCitationsForMessage,
      addBackgroundJob,
      updateBackgroundJob,
      subscribeToJobStream,
      updateToolCardForJob,
      isCurrentConversation,
      flushContentBuffer,
    ],
  );

  return { sendWithFiles, isUploading, cancelLocalStream };
}
